import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Plus, Search } from 'lucide-react';
import { aiRegistryClient } from '../../services/ai-registry-client';

export interface AIModel {
  id: string;
  provider: 'google' | 'openai' | 'anthropic' | 'other';
  name: string;
  version: string;
  capabilities: {
    streaming: boolean;
    thinking: boolean;
    vision: boolean;
    functionCalling: boolean;
    maxTokens: number;
    contextWindow: number;
  };
  pricing: {
    inputTokensPerMillion: number;
    outputTokensPerMillion: number;
    currency: string;
  };
  performance: {
    avgLatencyMs: number;
    p95LatencyMs: number;
    p99LatencyMs: number;
  };
  status: 'active' | 'deprecated' | 'beta';
  metadata: {
    description?: string;
    documentationUrl?: string;
  };
}

export function ModelsMain() {
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const loadModels = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (providerFilter) filters.provider = providerFilter;
      if (statusFilter) filters.status = statusFilter as 'active' | 'deprecated' | 'beta';

      const data = await aiRegistryClient.getModels(filters);
      setModels(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
      console.error('Error loading models:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModels();
  }, [providerFilter, statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadModels();
  };

  const filteredModels = models.filter(model => {
    const matchesSearch =
      !searchTerm ||
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'beta':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'deprecated':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'openai':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'anthropic':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Cpu className="w-8 h-8 text-uw-interactive-primary" />
          <h1 className="text-3xl font-bold text-uw-text-primary">AI Models</h1>
        </div>
        <Link
          to="/ai/models/new"
          className="inline-flex items-center px-4 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Register Model
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-uw-text-tertiary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search models..."
                className="w-full pl-10 pr-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Provider
            </label>
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="w-full px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
            >
              <option value="">All Providers</option>
              <option value="google">Google</option>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="beta">Beta</option>
              <option value="deprecated">Deprecated</option>
            </select>
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Model List */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uw-interactive-primary"></div>
          <p className="mt-4 text-uw-text-secondary">Loading models...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {!loading && !error && filteredModels.length === 0 && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-12 text-center">
          <Cpu className="w-16 h-16 text-uw-text-tertiary mx-auto mb-4" />
          <p className="text-uw-text-secondary">No models found</p>
        </div>
      )}

      {!loading && !error && filteredModels.length > 0 && (
        <div className="space-y-4">
          {filteredModels.map((model) => (
            <Link
              key={model.id}
              to={`/ai/models/${model.id}`}
              className="block bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6 hover:shadow-lg hover:border-uw-interactive-primary transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-uw-text-primary">
                      {model.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getProviderColor(model.provider)}`}>
                      {model.provider}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(model.status)}`}>
                      {model.status}
                    </span>
                  </div>
                  {model.metadata.description && (
                    <p className="text-sm text-uw-text-secondary mb-3">
                      {model.metadata.description}
                    </p>
                  )}
                  <div className="flex items-center gap-6 text-sm text-uw-text-tertiary">
                    <span>Context: {model.capabilities.contextWindow.toLocaleString()} tokens</span>
                    <span>Avg Latency: {model.performance.avgLatencyMs}ms</span>
                    <span>
                      Cost: ${model.pricing.inputTokensPerMillion}/M in, $
                      {model.pricing.outputTokensPerMillion}/M out
                    </span>
                    <div className="flex gap-2">
                      {model.capabilities.streaming && (
                        <span className="px-2 py-0.5 bg-uw-surface-elevated text-uw-text-secondary rounded text-xs">
                          Streaming
                        </span>
                      )}
                      {model.capabilities.thinking && (
                        <span className="px-2 py-0.5 bg-uw-surface-elevated text-uw-text-secondary rounded text-xs">
                          Thinking
                        </span>
                      )}
                      {model.capabilities.vision && (
                        <span className="px-2 py-0.5 bg-uw-surface-elevated text-uw-text-secondary rounded text-xs">
                          Vision
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
